import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

const BookCover = () => {
  const { t } = useI18n();
  const [rot, setRot] = useState({ x: 6, y: -12 });
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width;
    const dy = (e.clientY - cy) / rect.height;
    setRot({ x: 6 - dy * 14, y: -12 + dx * 22 });
  };
  const onLeave = () => setRot({ x: 6, y: -12 });

  return (
    <div
      className="book-scene relative w-[280px] h-[380px] md:w-[340px] md:h-[460px] lg:w-[400px] lg:h-[540px]"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <div
        className="book-cover-3d relative w-full h-full rounded-[6px]"
        style={{
          transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
          transition: "transform 0.5s cubic-bezier(.2,.8,.2,1)",
          boxShadow:
            "0 60px 100px -30px rgba(0,0,0,0.55), 0 20px 40px -20px rgba(230,92,0,0.35)",
        }}
      >
        <div className="book-spine" />
        <div
          className="cover-face rounded-[6px] overflow-hidden"
          style={{ background: "linear-gradient(155deg, #0F1F17 0%, #1A3A2A 55%, #0A231A 100%)" }}
        >
          <div className="relative w-full h-full p-6 md:p-8 flex flex-col justify-between text-[var(--cream)]">
            <div className="flex items-start justify-between">
              <div className="font-mono-caption text-[10px] tracking-[0.25em] uppercase opacity-70">
                {t.hero.book_edition}
              </div>
              <div className="w-8 h-8 rounded-full bg-[var(--marigold)] flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2v20M2 12h20" stroke="#0F1F17" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            <div className="relative flex-1 flex items-center justify-center">
              <svg viewBox="0 0 200 200" className="w-[70%] h-auto" aria-hidden="true">
                <defs>
                  <linearGradient id="dropg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FDFBF7" />
                    <stop offset="100%" stopColor="#E7CFA1" />
                  </linearGradient>
                </defs>
                <path
                  d="M100 20c22 30 42 55 42 82a42 42 0 1 1-84 0c0-27 20-52 42-82z"
                  fill="url(#dropg)"
                />
                <circle cx="100" cy="115" r="14" fill="#E65C00" />
                <text
                  x="100"
                  y="122"
                  textAnchor="middle"
                  fontFamily="Fraunces, serif"
                  fontSize="20"
                  fontWeight="700"
                  fill="#0F1F17"
                >
                  $3
                </text>
              </svg>
            </div>

            <div>
              <div className="font-mono-caption text-[10px] tracking-[0.25em] uppercase opacity-70 mb-3">
                {t.hero.book_pages}
              </div>
              <h3 className="font-display text-3xl md:text-4xl leading-[1] tracking-tight">
                {t.hero.book_title_1}
                <br />
                <span className="font-editorial-italic text-[var(--marigold-2)]">
                  {t.hero.book_title_link}
                </span>{" "}
                {t.hero.book_title_2}
              </h3>
              <div className="mt-4 flex items-center gap-2 text-[11px] opacity-75">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--marigold)]" />
                <span>{t.hero.book_watermark}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
        animate={{ opacity: 1, scale: 1, rotate: -8 }}
        transition={{ delay: 1.4, duration: 0.6, type: "spring", stiffness: 120 }}
        className="absolute top-4 right-4 md:-top-8 md:-right-10 bg-[var(--marigold)] text-[var(--cream)] rounded-full w-20 h-20 md:w-28 md:h-28 flex flex-col items-center justify-center shadow-2xl z-20"
        data-testid="hero-price-badge"
      >
        <span className="font-mono-caption text-[10px] tracking-[0.2em] uppercase opacity-90">
          {t.hero.badge_just}
        </span>
        <span className="font-display text-3xl md:text-5xl leading-none">$3</span>
        <span className="font-mono-caption text-[9px] tracking-[0.2em] uppercase opacity-90 mt-1">
          {t.hero.badge_was}
        </span>
      </motion.div>
    </div>
  );
};

const RevealLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onBuy }) {
  const { locale, t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [0, 220]), {
    stiffness: 80,
    damping: 20,
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] pt-28 md:pt-32 pb-20 md:pb-24 overflow-hidden grain"
      style={{
        background:
          "radial-gradient(1200px 700px at 85% 15%, #1A3A2A 0%, #0F1F17 55%, #08160F 100%)",
      }}
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute top-1/3 -left-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        aria-hidden="true"
      >
        <div className="w-full h-full rounded-full bg-[var(--marigold)] opacity-[0.09] blur-3xl" />
      </motion.div>

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#FDFBF7 1px, transparent 1px), linear-gradient(90deg, #FDFBF7 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="container-x relative z-10">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div className="text-[var(--cream)]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-[rgba(253,251,247,0.2)] bg-[rgba(253,251,247,0.05)] px-3 py-1.5 backdrop-blur-sm"
            >
              <span className="w-2 h-2 rounded-full bg-[var(--marigold)] animate-pulse" />
              <span className="font-mono-caption text-[10px] tracking-[0.22em] uppercase opacity-90">
                {t.hero.badge}
              </span>
            </motion.div>

            {/* Key each headline on locale so it re-animates on switch */}
            <AnimatePresence mode="wait">
              <motion.h1
                key={locale}
                className="font-display font-medium mt-6 text-[4rem] sm:text-[6rem] lg:text-[8.5rem] leading-[0.95] tracking-[-0.035em]"
                data-testid="hero-headline"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <RevealLine delay={0.2}>
                  {t.hero.h1_a}{" "}
                  <span className="font-editorial-italic text-[var(--marigold-2)]">
                    {t.hero.h1_italic}
                  </span>
                </RevealLine>
              </motion.h1>
            </AnimatePresence>

            <motion.p
              key={"p-" + locale}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-7 max-w-[560px] text-[var(--cream)]/75 text-base md:text-lg leading-relaxed"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={onBuy}
                className="btn-pill btn-primary text-[15px]"
                data-testid="hero-buy-button"
              >
                <span>{t.hero.cta_buy}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <a
                href="#asha"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#asha")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-pill btn-ghost-light text-[15px]"
                data-testid="hero-read-asha-button"
              >
                {t.hero.cta_read}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-[var(--cream)]/60 text-[12px]"
            >
              {[t.hero.trust1, t.hero.trust2, t.hero.trust3].map((tr, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12l4 4L19 6"
                      stroke="var(--marigold)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  {tr}
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            style={{ y }}
            className="flex justify-center lg:justify-end relative"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <BookCover />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.7 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-2 border-t border-[rgba(253,251,247,0.14)] pt-10"
        >
          {t.hero.metrics.map((s, i) => (
            <div key={i} className="text-[var(--cream)]">
              <div className="font-display text-4xl md:text-5xl leading-none tracking-tight">
                {s.k}
              </div>
              <div className="mt-2 text-[13px] text-[var(--cream)]/60 max-w-[200px] leading-snug">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
